import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { PaymentEventDTO } from "@/modules/payments/domain/contracts";
import { formatDateLocal, formatTimeLocal } from "@/shared/lib/date-time";
import { formatPriceLak } from "@/shared/lib/format-price";
import { AppImage } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { ImagePreviewModal } from "@/shared/ui/ImagePreviewModal";
import { QueryState } from "@/shared/ui/QueryState";
import { usePaymentQuery } from "../api/queries";

const STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າ",
  paid: "ຊຳລະແລ້ວ",
  failed: "ລົ້ມເຫຼວ",
  partially_refunded: "ຄືນບາງສ່ວນ",
  refunded: "ຄືນເງິນ",
  cancelled: "ຍົກເລີກ",
};

const STATUS_BADGE_VARIANTS: Record<
  string,
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "muted"
  | "accent"
  | "success"
  | "warning"
  | "info"
> = {
  pending: "warning",
  paid: "success",
  failed: "destructive",
  partially_refunded: "info",
  refunded: "secondary",
  cancelled: "muted",
};

function truncateId(id: string | null, len = 8): string {
  if (!id) return "—";
  if (id.length <= len * 2 + 2) return id;
  return `${id.slice(0, len)}...${id.slice(-len)}`;
}

function PaymentEventRow({ event }: { event: PaymentEventDTO }) {
  const [open, setOpen] = useState(false);
  const payloadStr = JSON.stringify(event.payload, null, 2);

  return (
    <tr className="border-b">
      <td className="py-2 font-mono text-muted-foreground text-sm">
        {formatTimeLocal(event.createdAt)}
      </td>
      <td className="py-2">
        <Badge variant="secondary">{event.eventType}</Badge>
      </td>
      <td className="py-2 font-mono text-xs">
        {truncateId(event.idempotencyKey, 4)}
      </td>
      <td className="py-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-primary text-sm hover:underline"
        >
          ເບິ່ງ Payload
        </button>
        <Modal
          open={open}
          onOpenChange={setOpen}
          title={event.eventType}
          size="lg"
        >
          <pre className="max-h-96 overflow-auto rounded bg-muted p-4 font-mono text-xs">
            {payloadStr}
          </pre>
        </Modal>
      </td>
    </tr>
  );
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 60) return `${diffMins}ນາທີກ່ອນ`;
  if (diffHours < 24) return `${diffHours}ຊົ່ວໂມງກ່ອນ`;
  if (diffDays < 7) return `${diffDays}ວັນກ່ອນ`;
  return formatDateLocal(iso);
}

export function PaymentDetailPage() {
  const { id } = useParams({ from: "/app/payments/$id" });
  const nav = useNavigate({ from: "/app/payments/$id" });
  const { data: payment, ...result } = usePaymentQuery(id);

  const slipItems = useMemo(() => {
    if (!payment) return [];
    const rows = (payment.paymentSlips ?? []).map((s) => ({
      id: s.id,
      fileKey: s.fileKey,
      sortOrder: s.sortOrder,
    }));
    const keys = new Set(rows.map((r) => r.fileKey));
    const mk = payment.metadata?.slipFileKey;
    if (typeof mk === "string" && mk.trim() && !keys.has(mk.trim())) {
      rows.push({
        id: `meta-slip-${mk.trim()}`,
        fileKey: mk.trim(),
        sortOrder: -1,
      });
    }
    return rows.sort((a, b) => a.sortOrder - b.sortOrder);
  }, [payment]);

  const breadcrumbLabel =
    payment?.orderCode ?? (id ? truncateId(id, 6) : "ລາຍລະອຽດ");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການຊຳລະ", to: "/app/payments" },
            { label: breadcrumbLabel },
          ]}
        />
      </Header>
      <Main>
        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ"
          description="ກຳລັງດຶງລາຍລະອຽດການຊຳລະ"
          variant="fullscreen"
        >
          {!payment ? null : (
            <div className="space-y-6">
              {/* Toolbar: ແບບเดียวกับ OrderDetailPage, ProductDetailPage */}
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    ຄືນແລ້ວ: LAK{" "}
                    {formatPriceLak(payment.refundSucceededTotal ?? 0)}
                  </p>
                  <div className="flex items-center gap-3">
                    <h1 className="font-bold text-2xl tracking-tight">
                      ການຊຳລະ #{payment.orderCode ?? truncateId(payment.id)}
                    </h1>
                    <Badge
                      variant={
                        STATUS_BADGE_VARIANTS[payment.status] ?? "default"
                      }
                      className="font-medium"
                    >
                      {STATUS_LABELS[payment.status] ?? payment.status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-muted-foreground text-sm">
                    LAK {formatPriceLak(payment.amount)}
                    {payment.paidAt && (
                      <>
                        {" • "}
                        ຊຳລະແລ້ວເມື່ອ {formatDateLocal(payment.paidAt)} ເວລາ{" "}
                        {formatTimeLocal(payment.paidAt)}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => nav({ to: "/app/payments" })}
                  >
                    <ArrowLeftIcon className="size-4" />
                    ກັບຄືນ
                  </Button>
                  {/* <Button variant="outline" disabled title="ມາພ້ອມພ້ອມ">
                    <ReceiptIcon className="size-4" />
                    ເບິ່ງໃບບິນ
                  </Button> */}
                  {/* <Button
                    variant="default"
                    disabled={payment.status !== "paid"}
                    title={payment.status === "paid" ? "ຄືນເງິນ" : "ຊຳລະແລ້ວເທົ່ານັ້ນ"}
                  >
                    <RotateCcwIcon className="size-4" />
                    ຄືນເງິນ
                  </Button> */}
                </div>
              </div>

              {/* Two columns: Event Log (left) | Link Gen + Notes (right) */}
              <div className="grid gap-6 lg:grid-cols-3">
                {/* Payment Event Log */}
                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        ລາຍການຄືນເງິນ (ບັນທຶກ)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {!payment.refunds || payment.refunds.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                          ບໍ່ມີລາຍການຄືນ
                        </p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {payment.refunds.map((r) => (
                            <li
                              key={r.id}
                              className="flex flex-wrap justify-between gap-2 rounded border bg-muted/30 px-2 py-1"
                            >
                              <span>LAK {formatPriceLak(r.amount)}</span>
                              <span className="text-muted-foreground text-xs">
                                {r.status} • {formatDateLocal(r.createdAt)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                  <Card className="mt-4">
                    <CardHeader>
                      <CardTitle className="text-base">ປະຫວັດ Event</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {payment.events.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                          ບໍ່ມີ event
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-left">
                                <th className="pb-2 font-medium text-muted-foreground">
                                  ເວລາ
                                </th>
                                <th className="pb-2 font-medium text-muted-foreground">
                                  ປະເພດ Event
                                </th>
                                <th className="pb-2 font-medium text-muted-foreground">
                                  ລະຫັດ Idempotency
                                </th>
                                <th className="pb-2 font-medium text-muted-foreground">
                                  ການກະທຳ
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...payment.events].reverse().map((e) => (
                                <PaymentEventRow key={e.id} event={e} />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                  {slipItems.length > 0 ? (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">ສະລິບໂອນເງິນ</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {slipItems.map((s) => (
                            <ImagePreviewModal
                              key={s.id}
                              fileKey={s.fileKey}
                              title="ສະລິບໂອນເງິນ"
                              triggerClassName="block w-full overflow-hidden rounded-md border bg-muted/20 p-0 text-left outline-none ring-offset-background transition-opacity hover:opacity-95 focus-visible:ring-2 focus-visible:ring-ring cursor-zoom-in"
                            >
                              <AppImage
                                src={s.fileKey}
                                alt=""
                                className="max-h-72 w-full object-contain"
                                showLoading
                              />
                            </ImagePreviewModal>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                  {/* Payment Link Generation */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">ສ້າງລິ້ງຊຳລະ</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <span className="mb-1 block text-muted-foreground text-xs">
                          ເລກຄຳສັ່ງ
                        </span>
                        <div className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm">
                          {payment.orderCode ?? payment.orderId}
                        </div>
                      </div>
                      <div>
                        <span className="mb-1 block text-muted-foreground text-xs">
                          ຍອດ
                        </span>
                        <div className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm">
                          LAK {formatPriceLak(payment.amount)}
                        </div>
                      </div>
                      {/* <Link
                        to="/app/orders/$id"
                        params={{ id: payment.orderId }}
                        className="flex w-full"
                      >
                        <Button
                          className="w-full"
                          variant="secondary"
                          size="sm"
                        >
                          <ExternalLinkIcon className="size-4" />
                          ສ້າງລິ້ງ ແລະ ໄປໜ້າຊຳລະ
                        </Button>
                      </Link> */}
                    </CardContent>
                  </Card>

                  {/* Internal Notes */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">ໝາຍເຫດພາຍໃນ</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground text-sm">
                        {(payment.metadata as { notes?: string })?.notes ??
                          "ບໍ່ມີໝາຍເຫດ"}
                      </p>
                      <div className="mt-3 flex items-center justify-between text-muted-foreground text-xs">
                        <span>
                          ອັບເດດ: {formatRelativeTime(payment.updatedAt)}
                        </span>
                        {/* <button
                          type="button"
                          className="text-primary hover:underline"
                          disabled
                          title="ມາພ້ອມພ້ອມ"
                        >
                          ແກ້ໄຂໝາຍເຫດ
                        </button> */}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
